import React from 'react'

import { LinkOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

/**
 * SourceLink Component
 *
 * Renders a clickable link to the source record based on source_type
 *
 * Mapping（InventoryMovement 的真實 source 只會是這五種，見 inventory_movement.rb）：
 * - ReceiptItem         -> /inbounds/:inbound_id
 * - FulfillmentItem     -> /fulfillments/:fulfillment_id
 * - StockTakingItem     -> /stock_takings/:stock_taking_id
 * - WorkOrderItem       -> /work_orders/:work_order_id
 * - StockAdjustmentItem -> /stock_adjustments/:stock_adjustment_id
 *
 * 連結與否依 role 而定：operator 端有上述各來源的詳情頁，故可點；
 * admin 端除了 ReceiptItem（入庫單 admin 也有詳情頁）外其餘來源無對應頁面，
 * 故只顯示純文字，避免連到不存在的路由。
 *
 * Note: 揀貨只產生 ShelfMovement，不會產生 InventoryMovement，
 * 故來源不會是 PickingListItem。
 */
export default function SourceLink({
  sourceType,
  sourceParentId,
  role = 'admin'
}) {
  const { t } = useTranslation()

  // Return '-' if no source
  if (!sourceType || !sourceParentId) {
    return <span>-</span>
  }

  // Map source_type to route
  const getRouteInfo = () => {
    switch (sourceType) {
      case 'ReceiptItem':
        return {
          path: `/inbounds/${sourceParentId}`,
          label: t('enums.inventory_history.source_type.ReceiptItem'),
          hasPage: true
        }
      case 'StockTakingItem':
        return {
          path: `/stock_takings/${sourceParentId}`,
          label: t('enums.inventory_history.source_type.StockTakingItem'),
          hasPage: role === 'operator'
        }
      case 'FulfillmentItem':
        return {
          path: `/fulfillments/${sourceParentId}`,
          label: t('enums.inventory_history.source_type.FulfillmentItem'),
          hasPage: role === 'operator'
        }
      case 'WorkOrderItem':
        return {
          path: `/work_orders/${sourceParentId}`,
          label: t('enums.inventory_history.source_type.WorkOrderItem'),
          hasPage: role === 'operator'
        }
      case 'StockAdjustmentItem':
        return {
          path: `/stock_adjustments/${sourceParentId}`,
          label: t('enums.inventory_history.source_type.StockAdjustmentItem'),
          hasPage: role === 'operator'
        }
      case 'TransferItem':
      case 'ManualInventory':
        return {
          path: null,
          label: t(`enums.inventory_history.source_type.${sourceType}`),
          hasPage: false
        }
      default:
        return {
          path: null,
          label: sourceType,
          hasPage: false
        }
    }
  }

  const routeInfo = getRouteInfo()

  // If no page exists, just show the label
  if (!routeInfo.hasPage || !routeInfo.path) {
    return <span>{routeInfo.label}</span>
  }

  // Render clickable link
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%'
    }}>
      <Link to={routeInfo.path} style={{ wordBreak: 'break-word' }}>
        {routeInfo.label}
      </Link>
      <a
        href={`/${role}${routeInfo.path}`}
        target="_blank"
        onClick={(e) => e.stopPropagation()}
        style={{ flexShrink: 0, marginLeft: '8px' }} rel="noreferrer"
      >
        <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
      </a>
    </div>
  )
}
